import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import ProfileSectionFrame from "containers/user-profile/profile-section-frame";
import { SECONDARY_BUTTON } from "containers/settings/ui/buttonStyles";
import { fetchCabinetSummaryThunk, JUST_ASKED_MS } from "./cabinet-summary.slice";
import { useCabinetSummary } from "./use-cabinet-summary";
import MyTaskGroups from "./my-task-groups";
import QueueCards from "./queue-cards";
import FillInTiles from "./fill-in-tiles";
import WaitingOthersList from "./waiting-others-list";

/**
 * «Užduotys» (/app/:companyId/my-work): everything that waits for the person, from
 * the cabinet summary — their own tasks, the work queues they serve, the documents
 * they can start, and their requests that wait for others. A part with nothing in it
 * is not drawn.
 */
export default function CabinetTasksPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const userId = useAppSelector((state) => state.authReducer.session?.id);
  const { summary, failed } = useCabinetSummary(companyId);

  // The dashboard keeps the summary fresh; opening the page asks once more, so what it
  // shows is never older than the visit. Not when a request has just gone out, as on a
  // cold load of this address, where the dashboard has already asked.
  useEffect(() => {
    if (companyId) dispatch(fetchCabinetSummaryThunk(companyId, { maxAgeMs: JUST_ASKED_MS }));
  }, [dispatch, companyId]);

  if (!companyId || !userId) return null;

  const status = summary
    ? summary.count > 0
      ? t("tasks_status_waiting", { count: summary.count })
      : t("tasks_status_all_clear")
    : "";

  return (
    <ProfileSectionFrame
      title={t("tasks")}
      hint={status}
      action={
        <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
          {t("tasks_all_my_companies")}
        </span>
      }
    >
      {summary ? (
        <div className="space-y-8">
          <MyTaskGroups summary={summary} companyId={companyId} userId={userId} />
          <QueueCards queues={summary.queues} companyId={companyId} />
          <FillInTiles
            companyId={companyId}
            userId={userId}
            hasFuel={summary.capabilities.hasFuel !== false}
          />
          <WaitingOthersList waiting={summary.waitingOthers} />
        </div>
      ) : failed ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-700">{t("tasks_load_failed")}</p>
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => dispatch(fetchCabinetSummaryThunk(companyId))}
          >
            {t("retry")}
          </button>
        </div>
      ) : (
        <div role="status" aria-label={t("loading")} className="space-y-3">
          <div className="h-16 animate-pulse rounded-lg bg-gray-200" />
          <div className="h-16 animate-pulse rounded-lg bg-gray-200" />
          <div className="h-16 animate-pulse rounded-lg bg-gray-200" />
        </div>
      )}
    </ProfileSectionFrame>
  );
}
